import { devDebug } from "@/lib/log";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCircle, Package, CurrencyEur, Warning, X } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/utils-format";

const ICONS = {
  order_created: Package,
  proof_uploaded: CurrencyEur,
  payment_approved: CheckCircle,
  collection_overdue: Warning,
};

const COLORS = {
  order_created: "text-blue-700 bg-blue-50 border-blue-200",
  proof_uploaded: "text-amber-700 bg-amber-50 border-amber-200",
  payment_approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
  collection_overdue: "text-red-700 bg-red-50 border-red-200",
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch (e) { devDebug("Notifications poll failed", e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleClick = async (n) => {
    if (!n.live && !n.read) await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    if (n.order_id) navigate(`/orders/${n.order_id}`);
    else if (n.account_id) navigate(`/receivers`);
    setOpen(false);
    load();
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md hover:bg-zinc-100 transition-colors"
        data-testid="notifications-bell-button"
        aria-label="Notifications"
      >
        <Bell size={20} weight={data.unread_count > 0 ? "fill" : "regular"} />
        {data.unread_count > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-unread-count"
          >
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[520px] bg-white border border-zinc-200 shadow-lg z-50 flex flex-col"
          data-testid="notifications-panel"
        >
          <div className="p-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Notifications</p>
              <p className="text-sm font-bold">{data.unread_count} unread</p>
            </div>
            <div className="flex items-center gap-2">
              {data.unread_count > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[10px] uppercase tracking-widest text-zinc-600 hover:text-zinc-900 font-semibold"
                  data-testid="notifications-mark-all-read"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-100 rounded" data-testid="notifications-close">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {data.notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">No notifications yet.</div>
            ) : (
              data.notifications.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                const colorClass = COLORS[n.type] || "text-zinc-700 bg-zinc-50 border-zinc-200";
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left p-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors flex gap-3 ${!n.read ? "bg-zinc-50/50" : ""}`}
                    data-testid={`notification-${n.notification_id}`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center border ${colorClass}`}>
                      <Icon size={14} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate">{n.title}</p>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1 font-mono">{formatDateTime(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
